import React from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Cart() {
  const { items, updateQuantity, removeItem, total } = useCart();

  if (items.length === 0) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-24 text-center" data-testid="cart-empty">
        <div className="grid h-20 w-20 place-items-center rounded-full bg-sky-50 text-sky-400">
          <ShoppingBag className="h-9 w-9" />
        </div>
        <h1 className="mt-6 font-display text-2xl font-black text-slate-900">Coșul tău este gol</h1>
        <p className="mt-2 text-slate-500">Descoperă detergenții și balsamurile noastre concentrate.</p>
        <Button asChild size="lg" className="mt-7 rounded-full bg-sky-600 px-7 hover:bg-sky-500">
          <Link to="/produse">Vezi produsele</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 lg:px-12" data-testid="cart-page">
      <h1 className="mb-8 font-display text-3xl font-black tracking-tight text-slate-900 lg:text-4xl">Coșul tău</h1>
      <div className="grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="divide-y divide-slate-100 rounded-2xl border border-slate-100 bg-white">
            {items.map((item) => (
              <div key={item.product_id} className="flex flex-wrap items-center gap-4 p-4 sm:p-5" data-testid={`cart-item-${item.product_id}`}>
                <div className="grid h-20 w-20 shrink-0 place-items-center rounded-xl bg-sky-50/60">
                  <img src={item.image} alt={item.name} className="h-16 w-auto object-contain" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-bold leading-snug text-slate-900">{item.name}</h3>
                  <p className="text-sm text-slate-500">{formatPrice(item.price)}</p>
                </div>
                <div className="flex w-full items-center justify-between gap-3 sm:w-auto">
                  <div className="flex items-center rounded-full border border-slate-200">
                    <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="grid h-9 w-9 place-items-center text-slate-500 hover:text-sky-600" data-testid={`cart-decrease-${item.product_id}`}>
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-bold">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="grid h-9 w-9 place-items-center text-slate-500 hover:text-sky-600" data-testid={`cart-increase-${item.product_id}`}>
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="font-display font-bold text-slate-900">
                    {formatPrice(item.price * item.quantity)}
                  </div>
                  <button onClick={() => removeItem(item.product_id)} className="text-slate-400 hover:text-red-500" data-testid={`cart-remove-${item.product_id}`}>
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="h-fit rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_32px_rgba(2,132,199,0.05)]">
          <h2 className="font-display text-lg font-bold text-slate-900">Sumar comandă</h2>
          <div className="mt-5 space-y-3 text-sm">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span data-testid="cart-subtotal">{formatPrice(total)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Transport</span><span className="font-bold text-emerald-600" data-testid="cart-shipping">GRATUIT</span></div>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700" data-testid="cart-free-shipping-note">
            <Truck className="h-4 w-4 shrink-0" /> Transport gratuit la toate comenzile!
          </div>
          <div className="my-5 h-px bg-slate-100" />
          <div className="flex items-center justify-between">
            <span className="font-display text-lg font-bold text-slate-900">Total</span>
            <span className="font-display text-2xl font-black text-slate-900" data-testid="cart-total">{formatPrice(total)}</span>
          </div>
          <Button asChild size="lg" className="mt-6 w-full rounded-full bg-sky-600 text-base hover:bg-sky-500" data-testid="cart-checkout-btn">
            <Link to="/finalizare">Finalizează comanda <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Link to="/produse" className="mt-4 block text-center text-sm font-semibold text-sky-600 hover:underline">
            Continuă cumpărăturile
          </Link>
        </div>
      </div>
    </div>
  );
}
